import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col, Card, Badge, Spinner, Button, Table } from 'react-bootstrap';

export default function ProposicaoDetalhes() {
    const [searchParams] = useSearchParams();
    const [proposicao, setProposicao] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dadosTecnicos, setDadosTecnicos] = useState(null);
    const [dadosHistorico, setDadosHistorico] = useState(null);
    const [expandedHistorico, setExpandedHistorico] = useState(false);
    const [expandedDatas, setExpandedDatas] = useState(false);
    const [expandedEmendas, setExpandedEmendas] = useState({});
    const [expandedPareceres, setExpandedPareceres] = useState({});
    const [expandedSubstitutivos, setExpandedSubstitutivos] = useState({});

    // Suporte aos novos parâmetros docid/tipoprop e aos antigos para compatibilidade
    const docid = searchParams.get('docid');
    const tipoprop = searchParams.get('tipoprop');
    const tipo = searchParams.get('tipo');
    const numero = searchParams.get('numero');
    const ano = searchParams.get('ano');
    
    // Mapeamento dos códigos tipoprop para tipos completos
    const tipoPropMapping = {
        'p': 'projeto',
        'i': 'indicacao',
        'pc': 'parecer',
        'r': 'requerimento'
    };
    
    // Mapeamento inverso para tipoprop
    const tipoToTipoProp = {
        'projeto': 'p',
        'indicacao': 'i', 
        'parecer': 'pc',
        'requerimento': 'r'
    };

    useEffect(() => {
        // Verifica se temos os novos parâmetros ou os antigos
        if (docid && tipoprop) {
            buscarDetalhes();
        } else if (tipo && numero && ano) {
            buscarDetalhes();
        } else {
            setError('Parâmetros inválidos para consulta. Use docid/tipoprop ou tipo/numero/ano');
            setLoading(false);
        }
    }, [docid, tipoprop, tipo, numero, ano]);

    const buscarDetalhes = async () => {
        setLoading(true);
        setError('');

        try {
            let url;
            
            if (docid && tipoprop) {
                // Usar novo sistema docid/tipoprop
                url = `https://dadosabertos.alepe.pe.gov.br/api/v1/proposicoes/projetos/?docid=${docid}&tipoprop=${tipoprop}`;
            } else {
                // Usar sistema legado numero/ano/tipo
                const tipoPlural = tipo + 's'; // Convert to plural for API
                url = `https://dadosabertos.alepe.pe.gov.br/api/v1/proposicoes/${tipoPlural}/?numero=${numero}&ano=${ano}`;
            }
            
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const xmlData = await response.text();
            
            // Parse the detailed XML
            const dadosDetalhados = extrairDadosDetalhadosCompletos(xmlData);
            
            // Determinar o tipo baseado nos parâmetros
            let tipoFinal;
            if (docid && tipoprop) {
                tipoFinal = tipoPropMapping[tipoprop] || tipoprop;
            } else {
                tipoFinal = tipo;
            }
            
            setProposicao({
                xml: xmlData,
                tipo: tipoFinal,
                dados: dadosDetalhados
            });
            
            // Extract all detailed data and separate them
            const todosOsDados = extrairDadosDetalhados(xmlData);
            setDadosTecnicos(separaDadosTecnicos(todosOsDados));
            setDadosHistorico(separaDadosHistorico(todosOsDados));
            
        } catch (error) {
            console.error('Erro ao carregar detalhes:', error);
            setError(`Erro ao carregar detalhes: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const extrairDadosDetalhadosCompletos = (xmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'text/xml');
        
        const mainElement = doc.querySelector('projeto') || doc.querySelector('indicacao') || doc.querySelector('requerimento') || doc.querySelector('parecer');
        
        let dados = {
            numero: 'N/A',
            ano: 'N/A',
            ementa: 'Ementa não disponível',
            situacao: 'N/A',
            autor: 'N/A',
            autores: [],
            dataCriacao: 'N/A',
            tipo: 'N/A',
            subtipo: 'N/A',
            legislatura: 'N/A',
            materia: 'N/A',
            justificativa: 'N/A',
            regimeTramitacao: 'N/A',
            impactoOrcamentario: 'N/A',
            resultadoFinal: 'N/A',
            numeroDpl: 'N/A',
            dataPublicacao: 'N/A',
            lotacaoAtual: 'N/A',
            primeiraInsercaoOd: 'N/A'
        };
        
        if (mainElement) {
            // Basic attributes
            dados.numero = mainElement.getAttribute('numero') || 'N/A';
            dados.ano = mainElement.getAttribute('ano') || 'N/A';
            dados.tipo = mainElement.getAttribute('tipo') || 'N/A';
            dados.subtipo = mainElement.getAttribute('subtipo') || 'N/A';
            dados.legislatura = mainElement.getAttribute('legislatura') || 'N/A';
            dados.situacao = mainElement.getAttribute('situacao') || 'N/A';
            
            // Extract project authors only (not from emendas)
            const autoresSection = mainElement.querySelector('autores');
            if (autoresSection) {
                // Get only direct children autor elements, not from nested emendas
                const autorElements = Array.from(autoresSection.children).filter(child => child.tagName === 'autor');
                dados.autores = autorElements.map(autor => ({
                    nome: autor.getAttribute('nome') || 'N/A',
                    tipo: autor.getAttribute('tipo') || 'N/A'
                }));
            } else {
                dados.autores = [];
            }
            
            // Main author for backward compatibility
            if (dados.autores.length > 0) {
                dados.autor = dados.autores[0].nome;
            }
            
            // Extract detailed content
            dados.ementa = getElementText(doc, 'ementa') || 'Ementa não disponível';
            dados.materia = getElementText(doc, 'materia') || 'N/A';
            dados.justificativa = getElementText(doc, 'justificativa') || 'N/A';

            // Extract HTML formatted content (new fields)
            dados.materiaHtml = getElementHTML(doc, 'materiaHtml') || null;
            dados.justificativaHtml = getElementHTML(doc, 'justificativaHtml') || null;
            dados.regimeTramitacao = getElementText(doc, 'regimeTramitacao') || 'N/A';
            dados.impactoOrcamentario = getElementText(doc, 'impactoOrcamentario') || 'N/A';
            dados.resultadoFinal = getElementText(doc, 'resultadoFinal') || 'N/A';
            dados.numeroDpl = getElementText(doc, 'numeroDpl') || 'N/A';
            dados.dataPublicacao = getElementText(doc, 'dataPublicacao') || 'N/A';
            dados.lotacaoAtual = getElementText(doc, 'lotacaoAtual') || 'N/A';

            // Extract committee opinions (pareceres) – now with full/HTML fields
            const pareceresElements = mainElement.querySelectorAll('pareceres parecer');
            dados.pareceres = Array.from(pareceresElements).map(parecer => ({
                numero: parecer.getAttribute('numero') || 'N/A',
                ano: parecer.getAttribute('ano') || 'N/A',
                comissao: parecer.getAttribute('comissao') || 'N/A',
                data: getElementTextFromNode(parecer, 'data') || 'N/A',
                tipo: getElementTextFromNode(parecer, 'tipo') || 'N/A',
                // previously supported
                relatorio: getElementTextFromNode(parecer, 'relatorio') || 'N/A',
                conclusao: getElementTextFromNode(parecer, 'conclusao') || 'N/A',
                // HTML variants
                relatorioHtml: getElementHTMLFromNode(parecer, 'relatorioHtml') || null,
                conclusaoHtml: getElementHTMLFromNode(parecer, 'conclusaoHtml') || null,
                // NEW: actual parecer content + HTML
                parecerRelator: getElementTextFromNode(parecer, 'parecerRelator') || 'N/A',
                parecerRelatorHtml: getElementHTMLFromNode(parecer, 'parecerRelatorHtml') || null
            }));

            // Extract amendments (emendas)
            const emendasElements = mainElement.querySelectorAll('emendas emenda');
            dados.emendas = Array.from(emendasElements).map(emenda => ({
                numero: emenda.getAttribute('numero') || 'N/A',
                ano: emenda.getAttribute('ano') || 'N/A',
                tipo: emenda.getAttribute('tipo') || 'N/A',
                legislatura: emenda.getAttribute('legislatura') || 'N/A',
                ementa: getElementTextFromNode(emenda, 'ementa') || 'N/A',
                justificativa: getElementTextFromNode(emenda, 'justificativa') || 'N/A',
                materia: getElementTextFromNode(emenda, 'materia') || 'N/A',
                // HTML formatted versions
                justificativaHtml: getElementHTMLFromNode(emenda, 'justificativaHtml') || null,
                materiaHtml: getElementHTMLFromNode(emenda, 'materiaHtml') || null,
                dataPublicacao: getElementTextFromNode(emenda, 'dataPublicacao') || 'N/A',
                lotacaoAtual: getElementTextFromNode(emenda, 'lotacaoAtual') || 'N/A',
                numeroDpl: getElementTextFromNode(emenda, 'numeroDpl') || 'N/A',
                autores: Array.from(emenda.querySelectorAll('autores autor')).map(autor => ({
                    nome: autor.getAttribute('nome') || 'N/A',
                    tipo: autor.getAttribute('tipo') || 'N/A'
                }))
            }));

            // Extract substitutivos (+ nested pareceres with full/HTML fields)
            const substitutivosElements = mainElement.querySelectorAll('substitutivos substitutivo');
            dados.substitutivos = Array.from(substitutivosElements).map(substitutivo => ({
                numero: substitutivo.getAttribute('numero') || 'N/A',
                ano: substitutivo.getAttribute('ano') || 'N/A',
                legislatura: substitutivo.getAttribute('legislatura') || 'N/A',
                ementa: getElementTextFromNode(substitutivo, 'ementa') || 'N/A',
                justificativa: getElementTextFromNode(substitutivo, 'justificativa') || 'N/A',
                materia: getElementTextFromNode(substitutivo, 'materia') || 'N/A',
                // HTML formatted versions
                justificativaHtml: getElementHTMLFromNode(substitutivo, 'justificativaHtml') || null,
                materiaHtml: getElementHTMLFromNode(substitutivo, 'materiaHtml') || null,
                autores: Array.from(substitutivo.querySelectorAll('autores autor')).map(autor => ({
                    nome: autor.getAttribute('nome') || 'N/A',
                    tipo: autor.getAttribute('tipo') || 'N/A'
                })),
                // Extract pareceres within substitutivo (now including HTML + actual content)
                pareceres: Array.from(substitutivo.querySelectorAll('pareceres parecer')).map(parecer => ({
                    numero: parecer.getAttribute('numero') || 'N/A',
                    ano: parecer.getAttribute('ano') || 'N/A',
                    comissao: parecer.getAttribute('comissao') || 'N/A',
                    data: getElementTextFromNode(parecer, 'data') || 'N/A',
                    tipo: getElementTextFromNode(parecer, 'tipo') || 'N/A',
                    relatorio: getElementTextFromNode(parecer, 'relatorio') || 'N/A',
                    conclusao: getElementTextFromNode(parecer, 'conclusao') || 'N/A',
                    relatorioHtml: getElementHTMLFromNode(parecer, 'relatorioHtml') || null,
                    conclusaoHtml: getElementHTMLFromNode(parecer, 'conclusaoHtml') || null,
                    parecerRelator: getElementTextFromNode(parecer, 'parecerRelator') || 'N/A',
                    parecerRelatorHtml: getElementHTMLFromNode(parecer, 'parecerRelatorHtml') || null
                }))
            }));

            // Extract creation date from datas section
            const dataElements = doc.querySelectorAll('datas data');
            for (let dataEl of dataElements) {
                if (dataEl.getAttribute('tipo') === 'criacao') {
                    dados.dataCriacao = dataEl.textContent.trim();
                    break;
                }
            }
            
            if (dados.dataCriacao === 'N/A') {
                dados.dataCriacao = getElementText(doc, 'dataApresentacao') ||
                                  getElementText(doc, 'data') ||
                                  'N/A';
            }
        }
        
        return dados;
    };

    const getElementText = (doc, tagName) => {
        const element = doc.querySelector(tagName);
        return element ? element.textContent.trim() : null;
    };

    const getElementHTML = (doc, tagName) => {
        const element = doc.querySelector(tagName);
        if (!element) return null;

        // Get the HTML content, handling CDATA sections
        const innerHTML = element.innerHTML.trim();
        if (innerHTML.startsWith('<![CDATA[') && innerHTML.endsWith(']]>')) {
            // Extract content from CDATA section
            return innerHTML.slice(9, -3).trim();
        }
        return innerHTML || null;
    };

    // Function specifically for extracting text from XML elements (like emenda nodes)
    const getElementTextFromNode = (xmlNode, tagName) => {
        const element = xmlNode.querySelector(tagName);
        return element ? element.textContent.trim() : null;
    };

    // Function specifically for extracting HTML from XML elements (like emenda nodes)
    const getElementHTMLFromNode = (xmlNode, tagName) => {
        const element = xmlNode.querySelector(tagName);
        if (!element) return null;

        // Get the HTML content, handling CDATA sections
        const innerHTML = element.innerHTML.trim();
        if (innerHTML.startsWith('<![CDATA[') && innerHTML.endsWith(']]>')) {
            // Extract content from CDATA section
            return innerHTML.slice(9, -3).trim();
        }
        return innerHTML || null;
    };

    const extrairDadosDetalhados = (xmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'text/xml');
        
        const dados = {
            datas: [],
            historico: [],
            distribuicao: []
        };
        
        // Extract dates
        const dataElements = doc.querySelectorAll('datas data');
        for (let dataEl of dataElements) {
            dados.datas.push({
                tipo: dataEl.getAttribute('tipo') || 'N/A',
                data: dataEl.textContent.trim()
            });
        }
        
        // Extract historical events
        const eventoElements = doc.querySelectorAll('historico evento');
        for (let evento of eventoElements) {
            dados.historico.push({
                data: evento.getAttribute('data') || 'N/A',
                acao: evento.textContent.trim()
            });
        }
        
        // Extract distribution information
        const distribElements = doc.querySelectorAll('distribuicao distribuido');
        for (let distrib of distribElements) {
            dados.distribuicao.push({
                comissao: distrib.getAttribute('comissao') || 'N/A',
                data: distrib.getAttribute('data') || 'N/A'
            });
        }
        
        return dados;
    };

    const separaDadosTecnicos = (dados) => {
        if (!dados) return null;
        
        const camposTecnicos = ['materia', 'justificativa', 'regime tramitacao', 'impacto orcamentario'];
        const dadosTecnicos = {};
        
        Object.keys(dados).forEach(key => {
            const keyLower = key.toLowerCase();
            if (camposTecnicos.some(campo => keyLower.includes(campo))) {
                dadosTecnicos[key] = dados[key];
            }
        });
        
        return Object.keys(dadosTecnicos).length > 0 ? dadosTecnicos : null;
    };

    const separaDadosHistorico = (dados) => {
        if (!dados) return null;
        
        const camposHistorico = ['datas', 'historico', 'resultado final', 'situacao atual', 'tramitacao'];
        const dadosHistorico = {};
        
        Object.keys(dados).forEach(key => {
            const keyLower = key.toLowerCase();
            if (camposHistorico.some(campo => keyLower.includes(campo))) {
                dadosHistorico[key] = dados[key];
            }
        });
        
        return Object.keys(dadosHistorico).length > 0 ? dadosHistorico : null;
    };

    const formatarChave = (chave) => {
        return chave
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/^\w/, c => c.toUpperCase());
    };

    const formatarData = (data) => {
        if (!data || data === 'N/A') return 'N/A';
        
        const formatos = [
            /^(\d{4})-(\d{2})-(\d{2})/,
            /^(\d{2})\/(\d{2})\/(\d{4})/,
            /^(\d{4})\/(\d{2})\/(\d{2})/
        ];
        
        for (let formato of formatos) {
            const match = data.match(formato);
            if (match) {
                let dia, mes, ano;
                if (formato.source.startsWith('^(\\d{4})')) {
                    ano = match[1];
                    mes = match[2];
                    dia = match[3];
                } else {
                    dia = match[1];
                    mes = match[2];
                    ano = match[3];
                }
                return `${dia}/${mes}/${ano}`;
            }
        }
        
        return data;
    };

    const getTipoNome = (tipo) => {
        const nomes = {
            'projeto': 'Projeto de Lei',
            'indicacao': 'Indicação',
            'requerimento': 'Requerimento',
            'parecer': 'Parecer'
        };
        return nomes[tipo] || tipo.charAt(0).toUpperCase() + tipo.slice(1);
    };

    const getTipoBadgeClass = (tipo) => {
        const classes = {
            'projeto': 'success',
            'indicacao': 'info',
            'requerimento': 'warning',
            'parecer': 'secondary'
        };
        return classes[tipo] || 'secondary';
    };

    const copiarDetalhes = async () => {
        if (!proposicao) return;
        
        const texto = `${getTipoNome(proposicao.tipo)} ${proposicao.dados.numero}/${proposicao.dados.ano}\n\n` +
            `Autor: ${proposicao.dados.autor}\n` +
            `Data: ${formatarData(proposicao.dados.dataCriacao)}\n` +
            // `Situação: ${proposicao.dados.situacao}\n\n` +
            `Ementa:\n${proposicao.dados.ementa}`;
        
        try {
            await navigator.clipboard.writeText(texto);
            alert('Detalhes copiados para a área de transferência!');
        } catch (err) {
            console.error('Erro ao copiar:', err);
            alert('Erro ao copiar detalhes.');
        }
    };

    const historicoComDatas = () => {
        if (!dadosHistorico || !dadosHistorico['datas'] || !dadosHistorico['historico']) {
            return [];
        }
        
        const datasTexto = dadosHistorico['datas'];
        const datas = datasTexto.match(/\d{2}\/\d{2}\/\d{4}/g) || [];
        
        const historicoTexto = dadosHistorico['historico'];
        const statusList = [];
        
        const statusPatterns = [
            'AGUARDANDO APOIAMENTO',
            'ENVIADO P/ SGMD',
            'ALTERAÇÃO DE COAUTOR',
            'NUMERADO',
            'DESPACHADO',
            'EMITIR PARECER',
            'ENVIADO PARA PUBLICAÇÃO',
            'PUBLICADO',
            'TRAMITANDO',
            'APROVADO',
            'REJEITADO',
            'ARQUIVADO'
        ];
        
        let remainingText = historicoTexto;
        
        statusPatterns.forEach(pattern => {
            if (remainingText.includes(pattern)) {
                statusList.push(pattern);
                remainingText = remainingText.replace(pattern, '|').replace(/\|+/g, '|');
            }
        });
        
        if (statusList.length === 0) {
            const matches = historicoTexto.match(/[A-Z][A-Z\s]*(?=[A-Z][A-Z\s]*|$)/g) || [];
            statusList.push(...matches.map(m => m.trim()).filter(m => m.length > 1));
        }
        
        const timeline = [];
        const maxLength = Math.max(datas.length, statusList.length);
        
        for (let i = 0; i < maxLength; i++) {
            const data = datas[i] || null;
            const status = statusList[i] || null;
            
            if (status && status.length > 1) {
                timeline.push({
                    data: data,
                    status: status
                });
            }
        }
        
        return timeline;
    };

    const dadosHistoricoSemTimeline = () => {
        if (!dadosHistorico) return {};

        const semTimeline = { ...dadosHistorico };
        delete semTimeline['datas'];
        delete semTimeline['historico'];

        return semTimeline;
    };

    const toggleEmenda = (index) => {
        setExpandedEmendas(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const toggleParecer = (index) => {
        setExpandedPareceres(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const toggleSubstitutivo = (index) => {
        setExpandedSubstitutivos(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    if (loading) {
        return (
            <Row>
                <Col xs="12" className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                    <div className="mt-3">
                        <p className="text-muted">Carregando detalhes da proposta legislativa...</p>
                    </div>
                </Col>
            </Row>
        );
    }

    if (error) {
        return (
            <Row>
                <Col xs="12">
                    <div className="alert alert-danger">
                        <strong>Erro:</strong> {error}
                    </div>
                    <Link to="/" className="btn btn-primary">
                        Voltar à Busca
                    </Link>
                </Col>
            </Row>
        );
    }

    if (!proposicao) {
        return (
            <Row>
                <Col xs="12">
                    <div className="alert alert-warning">
                        Proposta legislativa não encontrada.
                    </div>
                    <Link to="/" className="btn btn-primary">
                        Voltar à Busca
                    </Link>
                </Col>
            </Row>
        );
    }

    return (
        <Row>
            <Col xs="12">
                <p>
                    <small>
                        <Link to="/">Consulta de Propostas Legislativas</Link> / {getTipoNome(proposicao.tipo)} {proposicao.dados.numero}/{proposicao.dados.ano}
                    </small>
                </p>
            </Col>
            
            <Col xs="12" className="text-center mb-4">
                <h2>{getTipoNome(proposicao.tipo)} {proposicao.dados.numero}/{proposicao.dados.ano}</h2>
                {proposicao.dados.subtipo && proposicao.dados.subtipo !== 'N/A' && (
                    <h2 style={{ fontSize: '80%' }}>{proposicao.dados.subtipo}</h2>
                )}
            </Col>

            {/* Informações Gerais */}
            <Col xs="12" className='mt-3'>
                <div className="proposicao-list-item-header"><h3>Informações Gerais</h3></div>
                <div className="proposicao-list-item-text">
                    <Table>
                        <tbody>
                            <tr><td><strong>Número/Ano:</strong></td><td>{proposicao.dados.numero}/{proposicao.dados.ano}</td></tr>
                            {proposicao.dados.autor !== 'N/A' && (
                                <tr><td><strong>Autor Principal:</strong></td><td>{proposicao.dados.autor}</td></tr>
                            )}
                            {proposicao.dados.dataCriacao !== 'N/A' && (
                                <tr><td><strong>Data de Criação:</strong></td><td>{formatarData(proposicao.dados.dataCriacao)}</td></tr>
                            )}
                            {/* {proposicao.dados.situacao !== 'N/A' && (
                                <tr><td><strong>Situação:</strong></td><td>{proposicao.dados.situacao}</td></tr>
                            )} */}
                            {proposicao.dados.legislatura !== 'N/A' && (
                                <tr><td><strong>Legislatura:</strong></td><td>{proposicao.dados.legislatura}</td></tr>
                            )}
                            {proposicao.dados.regimeTramitacao !== 'N/A' && (
                                <tr><td><strong>Regime de Tramitação:</strong></td><td>{proposicao.dados.regimeTramitacao}</td></tr>
                            )}
                            {proposicao.dados.impactoOrcamentario !== 'N/A' && (
                                <tr><td><strong>Impacto Orçamentário:</strong></td><td>{proposicao.dados.impactoOrcamentario}</td></tr>
                            )}
                            {proposicao.dados.resultadoFinal !== 'N/A' && (
                                <tr><td><strong>Resultado Final:</strong></td><td>{proposicao.dados.resultadoFinal}</td></tr>
                            )}
                            {proposicao.dados.numeroDpl !== 'N/A' && (
                                <tr><td><strong>D.P.L.:</strong></td><td>{proposicao.dados.numeroDpl}</td></tr>
                            )}
                            {proposicao.dados.dataPublicacao !== 'N/A' && (
                                <tr><td><strong>Data de Publicação:</strong></td><td>{formatarData(proposicao.dados.dataPublicacao)}</td></tr>
                            )}
                            {/* {proposicao.dados.lotacaoAtual !== 'N/A' && (
                                <tr><td><strong>Localização:</strong></td><td>{proposicao.dados.lotacaoAtual}</td></tr>
                            )} */}
                        </tbody>
                    </Table>
                </div>
            </Col>

            {/* Autoria */}
            {proposicao.dados.autores && proposicao.dados.autores.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Autoria</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Tipo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proposicao.dados.autores.map((autor, index) => (
                                    <tr key={index}>
                                        <td>{autor.nome}</td>
                                        <td>{autor.tipo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            )}

            {/* Ementa */}
            <Col xs="12" className='mt-3'>
                <div className="proposicao-list-item-header"><h3>Ementa</h3></div>
                <div className="proposicao-list-item-text">
                    <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                        {proposicao.dados.ementa}
                    </p>
                </div>
            </Col>

            {/* Matéria */}
            {proposicao.dados.materia !== 'N/A' && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Matéria/Texto Completo</h3></div>
                    <div className="proposicao-list-item-text">
                        {proposicao.dados.materiaHtml ? (
                            <div
                                style={{ textAlign: 'justify' }}
                                dangerouslySetInnerHTML={{ __html: proposicao.dados.materiaHtml }}
                            />
                        ) : (
                            <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                {proposicao.dados.materia}
                            </p>
                        )}
                    </div>
                </Col>
            )}

            {/* Justificativa */}
            {proposicao.dados.justificativa !== 'N/A' && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Justificativa</h3></div>
                    <div className="proposicao-list-item-text">
                        {proposicao.dados.justificativaHtml ? (
                            <div
                                style={{ textAlign: 'justify' }}
                                dangerouslySetInnerHTML={{ __html: proposicao.dados.justificativaHtml }}
                            />
                        ) : (
                            <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                {proposicao.dados.justificativa}
                            </p>
                        )}
                    </div>
                </Col>
            )}

            {/* Emendas */}
            {proposicao.dados.emendas && proposicao.dados.emendas.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Emendas ({proposicao.dados.emendas.length})</h3></div>
                    <div className="proposicao-list-item-text">
                        {proposicao.dados.emendas
                            .sort((a, b) => parseInt(a.numero) - parseInt(b.numero))
                            .map((emenda, index) => (
                                <Card key={`emenda-${index}`} className="mb-3">
                                    <Card.Header
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => toggleEmenda(index)}
                                    >
                                        <div>
                                            <strong>Emenda {emenda.numero}/{emenda.ano}</strong>
                                            {emenda.autores && emenda.autores.length > 0 && (
                                                <span className="text-muted ms-2">- {emenda.autores[0].nome}</span>
                                            )}
                                        </div>
                                        <i className={`bi ${expandedEmendas[index] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                    </Card.Header>
                                    {expandedEmendas[index] && (
                                        <Card.Body>
                                            {/* Ementa */}
                                            {emenda.ementa !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Ementa:</h6>
                                                    <p style={{ textAlign: 'justify' }}>{emenda.ementa}</p>
                                                </div>
                                            )}

                                            {/* Matéria */}
                                            {emenda.materia !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Texto:</h6>
                                                    {emenda.materiaHtml ? (
                                                        <div
                                                            style={{ textAlign: 'justify' }}
                                                            dangerouslySetInnerHTML={{ __html: emenda.materiaHtml }}
                                                        />
                                                    ) : (
                                                        <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                            {emenda.materia}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Justificativa */}
                                            {emenda.justificativa !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Justificativa:</h6>
                                                    {emenda.justificativaHtml ? (
                                                        <div
                                                            style={{ textAlign: 'justify' }}
                                                            dangerouslySetInnerHTML={{ __html: emenda.justificativaHtml }}
                                                        />
                                                    ) : (
                                                        <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                            {emenda.justificativa}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Informações adicionais */}
                                            <div className="border-top pt-3">
                                                <Table size="sm">
                                                    <tbody>
                                                        {emenda.dataPublicacao !== 'N/A' && (
                                                            <tr>
                                                                <td><strong>Data de Publicação:</strong></td>
                                                                <td>{formatarData(emenda.dataPublicacao)}</td>
                                                            </tr>
                                                        )}
                                                        {/* {emenda.lotacaoAtual !== 'N/A' && (
                                                            <tr>
                                                                <td><strong>Localização:</strong></td>
                                                                <td>{emenda.lotacaoAtual}</td>
                                                            </tr>
                                                        )} */}
                                                        {emenda.numeroDpl !== 'N/A' && (
                                                            <tr>
                                                                <td><strong>D.P.L.:</strong></td>
                                                                <td>{emenda.numeroDpl}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                    )}
                                </Card>
                            ))}
                    </div>
                </Col>
            )}

            {/* Substitutivos */}
            {proposicao.dados.substitutivos && proposicao.dados.substitutivos.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Substitutivos ({proposicao.dados.substitutivos.length})</h3></div>
                    <div className="proposicao-list-item-text">
                        {proposicao.dados.substitutivos
                            .sort((a, b) => parseInt(a.numero) - parseInt(b.numero))
                            .map((substitutivo, index) => (
                                <Card key={`substitutivo-${index}`} className="mb-3">
                                    <Card.Header
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => toggleSubstitutivo(index)}
                                    >
                                        <div>
                                            <strong>Substitutivo {substitutivo.numero}/{substitutivo.ano}</strong>
                                            {substitutivo.autores && substitutivo.autores.length > 0 && (
                                                <span className="text-muted ms-2">- {substitutivo.autores[0].nome}</span>
                                            )}
                                        </div>
                                        <i className={`bi ${expandedSubstitutivos[index] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                    </Card.Header>
                                    {expandedSubstitutivos[index] && (
                                        <Card.Body>
                                            {/* Ementa */}
                                            {substitutivo.ementa !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Ementa:</h6>
                                                    <p style={{ textAlign: 'justify' }}>{substitutivo.ementa}</p>
                                                </div>
                                            )}

                                            {/* Matéria */}
                                            {substitutivo.materia !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Texto:</h6>
                                                    {substitutivo.materiaHtml ? (
                                                        <div
                                                            style={{ textAlign: 'justify' }}
                                                            dangerouslySetInnerHTML={{ __html: substitutivo.materiaHtml }}
                                                        />
                                                    ) : (
                                                        <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                            {substitutivo.materia}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Justificativa */}
                                            {substitutivo.justificativa !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Justificativa:</h6>
                                                    {substitutivo.justificativaHtml ? (
                                                        <div
                                                            style={{ textAlign: 'justify' }}
                                                            dangerouslySetInnerHTML={{ __html: substitutivo.justificativaHtml }}
                                                        />
                                                    ) : (
                                                        <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                            {substitutivo.justificativa}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Pareceres do Substitutivo */}
                                            {substitutivo.pareceres && substitutivo.pareceres.length > 0 && (
                                                <div className="border-top pt-3">
                                                    <h6>Pareceres ({substitutivo.pareceres.length}):</h6>
                                                    {substitutivo.pareceres
                                                        .sort((a, b) => parseInt(a.numero) - parseInt(b.numero))
                                                        .map((parecer, parecerIndex) => (
                                                            <Card key={`substitutivo-parecer-${parecerIndex}`} className="mb-2" style={{ backgroundColor: '#fafafa' }}>
                                                                <Card.Body>
                                                                    <div className="mb-2">
                                                                        <strong>Parecer {parecer.tipo?.toUpperCase?.() || ''} {parecer.numero}/{parecer.ano}</strong>
                                                                        <span className="text-muted ms-2">- {parecer.comissao}</span>
                                                                    </div>

                                                                    {/* Relatório */}
                                                                    {parecer.relatorioHtml ? (
                                                                        <div className="mb-2">
                                                                            <small><strong>Relatório:</strong></small>
                                                                            <div style={{ textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: parecer.relatorioHtml }} />
                                                                        </div>
                                                                    ) : (parecer.relatorio !== 'N/A' && (
                                                                        <div className="mb-2">
                                                                            <small><strong>Relatório:</strong></small>
                                                                            <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                                                                                {parecer.relatorio}
                                                                            </p>
                                                                        </div>
                                                                    ))}

                                                                    {/* Parecer do Relator (conteúdo principal) */}
                                                                    {parecer.parecerRelatorHtml ? (
                                                                        <div className="mb-2">
                                                                            <small><strong>Parecer do Relator:</strong></small>
                                                                            <div style={{ textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: parecer.parecerRelatorHtml }} />
                                                                        </div>
                                                                    ) : (parecer.parecerRelator !== 'N/A' && (
                                                                        <div className="mb-2">
                                                                            <small><strong>Parecer do Relator:</strong></small>
                                                                            <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                                                                                {parecer.parecerRelator}
                                                                            </p>
                                                                        </div>
                                                                    ))}

                                                                    {/* Conclusão */}
                                                                    {parecer.conclusaoHtml ? (
                                                                        <div className="mb-2">
                                                                            <small><strong>Conclusão:</strong></small>
                                                                            <div style={{ textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: parecer.conclusaoHtml }} />
                                                                        </div>
                                                                    ) : (parecer.conclusao !== 'N/A' && (
                                                                        <div className="mb-2">
                                                                            <small><strong>Conclusão:</strong></small>
                                                                            <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                                                                                {parecer.conclusao}
                                                                            </p>
                                                                        </div>
                                                                    ))}

                                                                    {/* Data */}
                                                                    {parecer.data !== 'N/A' && (
                                                                        <small className="text-muted">Data: {formatarData(parecer.data)}</small>
                                                                    )}
                                                                </Card.Body>
                                                            </Card>
                                                        ))}
                                                </div>
                                            )}
                                        </Card.Body>
                                    )}
                                </Card>
                            ))}
                    </div>
                </Col>
            )}

            {/* Pareceres */}
            {proposicao.dados.pareceres && proposicao.dados.pareceres.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Pareceres ({proposicao.dados.pareceres.length})</h3></div>
                    <div className="proposicao-list-item-text">
                        {proposicao.dados.pareceres
                            .sort((a, b) => parseInt(a.numero) - parseInt(b.numero))
                            .map((parecer, index) => (
                                <Card key={`parecer-${index}`} className="mb-3">
                                    <Card.Header
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => toggleParecer(index)}
                                    >
                                        <div>
                                            <strong>Parecer {parecer.tipo?.toUpperCase?.() || ''} {parecer.numero}/{parecer.ano}</strong>
                                            <span className="text-muted ms-2">- {parecer.comissao}</span>
                                        </div>
                                        <i className={`bi ${expandedPareceres[index] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                    </Card.Header>
                                    {expandedPareceres[index] && (
                                        <Card.Body>
                                            {/* Relatório */}
                                            {parecer.relatorioHtml ? (
                                                <div className="mb-3">
                                                    <h6>Relatório:</h6>
                                                    <div style={{ textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: parecer.relatorioHtml }} />
                                                </div>
                                            ) : (parecer.relatorio !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Relatório:</h6>
                                                    <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                        {parecer.relatorio}
                                                    </p>
                                                </div>
                                            ))}

                                            {/* Parecer do Relator (conteúdo principal) */}
                                            {parecer.parecerRelatorHtml ? (
                                                <div className="mb-3">
                                                    <h6>Parecer do Relator:</h6>
                                                    <div style={{ textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: parecer.parecerRelatorHtml }} />
                                                </div>
                                            ) : (parecer.parecerRelator !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Parecer do Relator:</h6>
                                                    <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                        {parecer.parecerRelator}
                                                    </p>
                                                </div>
                                            ))}

                                            {/* Conclusão */}
                                            {parecer.conclusaoHtml ? (
                                                <div className="mb-3">
                                                    <h6>Conclusão:</h6>
                                                    <div style={{ textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: parecer.conclusaoHtml }} />
                                                </div>
                                            ) : (parecer.conclusao !== 'N/A' && (
                                                <div className="mb-3">
                                                    <h6>Conclusão:</h6>
                                                    <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                                        {parecer.conclusao}
                                                    </p>
                                                </div>
                                            ))}

                                            {/* Informações adicionais */}
                                            <div className="border-top pt-3">
                                                <Table size="sm">
                                                    <tbody>
                                                        {parecer.data !== 'N/A' && (
                                                            <tr>
                                                                <td><strong>Data:</strong></td>
                                                                <td>{formatarData(parecer.data)}</td>
                                                            </tr>
                                                        )}
                                                        <tr>
                                                            <td><strong>Comissão:</strong></td>
                                                            <td>{parecer.comissao}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                    )}
                                </Card>
                            ))}
                    </div>
                </Col>
            )}

            {/* Datas Importantes */}
            {dadosHistorico?.datas && dadosHistorico.datas.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Datas Importantes</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosHistorico.datas
                                    .sort((a, b) => new Date(b.data) - new Date(a.data)) // Newest first
                                    .slice(0, expandedDatas ? dadosHistorico.datas.length : 3)
                                    .map((item, index) => (
                                        <tr key={index}>
                                            <td>{formatarChave(item.tipo)}</td>
                                            <td>{formatarData(item.data)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </Table>
                        {dadosHistorico.datas.length > 3 && (
                            <div className="text-center mt-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setExpandedDatas(!expandedDatas)}
                                >
                                    {expandedDatas ? (
                                        <>Mostrar menos <i className="bi bi-chevron-up"></i></>
                                    ) : (
                                        <>Mostrar todas ({dadosHistorico.datas.length}) <i className="bi bi-chevron-down"></i></>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>
            )}

            {/* Histórico de Tramitação */}
            {dadosHistorico?.historico && dadosHistorico.historico.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Histórico de Tramitação</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosHistorico.historico
                                    .sort((a, b) => new Date(b.data) - new Date(a.data)) // Newest first
                                    .slice(0, expandedHistorico ? dadosHistorico.historico.length : 3)
                                    .map((item, index) => (
                                        <tr key={index}>
                                            <td>{formatarData(item.data)}</td>
                                            <td>{item.acao}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </Table>
                        {dadosHistorico.historico.length > 3 && (
                            <div className="text-center mt-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setExpandedHistorico(!expandedHistorico)}
                                >
                                    {expandedHistorico ? (
                                        <>Mostrar menos <i className="bi bi-chevron-up"></i></>
                                    ) : (
                                        <>Mostrar todas ({dadosHistorico.historico.length}) <i className="bi bi-chevron-down"></i></>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>
            )}

            {/* Distribuição para Comissões */}
            {dadosHistorico?.distribuicao && dadosHistorico.distribuicao.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Distribuição para Comissões</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Comissão</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosHistorico.distribuicao.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.comissao}</td>
                                        <td>{formatarData(item.data)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            )}

            {/* Ações */}
            <Col xs="12" className='mt-5 mb-4 text-center'>
                <Link to="/" className="btn btn-primary me-3">
                    Voltar à Busca
                </Link>
                <button className="btn btn-outline-secondary" onClick={copiarDetalhes}>
                    Copiar Detalhes
                </button>
            </Col>
        </Row>
    );
}
